/**
 * ========================================
 * 品质保障信任模块 JavaScript
 * Trust Badges Section Scripts
 * Version: 1.0.0
 * ========================================
 */

(function() {
  'use strict';

  /**
   * 信任徽章动画控制器
   */
  class TrustBadgesAnimation {
    constructor() {
      this.sections = document.querySelectorAll('.trust-badges-section');
      this.observerOptions = {
        root: null,
        rootMargin: '0px 0px -100px 0px',
        threshold: 0.1
      };
      this.animatedSections = new Set();
      this.init();
    }

    init() {
      if (!this.sections.length) return;

      if ('IntersectionObserver' in window) {
        this.observeElements();
      } else {
        this.showAllElements();
      }

      this.addInteractiveEffects();
      this.addKeyboardNavigation();
      this.addButtonEffects();
    }

    observeElements() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.animatedSections.has(entry.target)) {
            this.animateItems(entry.target);
            this.animatedSections.add(entry.target);
            observer.unobserve(entry.target);
          }
        });
      }, this.observerOptions);

      this.sections.forEach(section => {
        observer.observe(section);
      });
    }

    animateItems(section) {
      const grid = section.querySelector('.trust-badges-grid');
      const items = section.querySelectorAll('.trust-badge-item');
      const animationType = grid ? grid.dataset.animation : 'none';

      if (animationType === 'none' || this.prefersReducedMotion()) {
        items.forEach(item => {
          item.style.opacity = '1';
          item.style.transform = 'none';
        });
        return;
      }

      items.forEach((item, index) => {
        const delay = parseInt(item.dataset.animationDelay) || 0;
        
        setTimeout(() => {
          item.classList.add('animated');
          
          item.addEventListener('animationend', () => {
            this.onAnimationComplete(item);
          }, { once: true });
        }, delay);
      });
    }

    onAnimationComplete(item) {
      item.style.opacity = '1';
      item.style.transform = 'none';
      
      const event = new CustomEvent('trustBadgeAnimationComplete', {
        detail: { element: item }
      });
      document.dispatchEvent(event);
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    showAllElements() {
      this.sections.forEach(section => {
        const items = section.querySelectorAll('.trust-badge-item');
        items.forEach(item => {
          item.style.opacity = '1';
          item.style.transform = 'none';
        });
      });
    }

    addInteractiveEffects() {
      const iconWrappers = document.querySelectorAll('.trust-badge-icon-wrapper');
      
      iconWrappers.forEach(wrapper => {
        wrapper.addEventListener('click', (e) => {
          this.createRippleEffect(e, wrapper);
        });

        wrapper.addEventListener('touchstart', () => {
          wrapper.style.opacity = '0.8';
        });

        wrapper.addEventListener('touchend', () => {
          wrapper.style.opacity = '1';
        });
      });
    }

    createRippleEffect(event, element) {
      if (!document.getElementById('ripple-animation-style')) {
        const style = document.createElement('style');
        style.id = 'ripple-animation-style';
        style.textContent = `
          @keyframes ripple-animation {
            to {
              transform: scale(2.5);
              opacity: 0;
            }
          }
          .ripple-effect {
            position: absolute;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.6);
            pointer-events: none;
            transform: scale(0);
            animation: ripple-animation 0.6s ease-out;
          }
        `;
        document.head.appendChild(style);
      }

      const ripple = document.createElement('span');
      const rect = element.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const x = event.clientX - rect.left - size / 2;
      const y = event.clientY - rect.top - size / 2;

      ripple.className = 'ripple-effect';
      ripple.style.width = `${size}px`;
      ripple.style.height = `${size}px`;
      ripple.style.left = `${x}px`;
      ripple.style.top = `${y}px`;

      const position = window.getComputedStyle(element).position;
      if (position === 'static') {
        element.style.position = 'relative';
      }
      element.style.overflow = 'hidden';
      
      element.appendChild(ripple);

      setTimeout(() => {
        ripple.remove();
      }, 600);
    }

    addKeyboardNavigation() {
      const badgeItems = document.querySelectorAll('.trust-badge-item');
      
      badgeItems.forEach((item, index) => {
        item.setAttribute('tabindex', '0');
        item.setAttribute('role', 'article');
        
        const title = item.querySelector('.trust-badge-title');
        if (title) {
          item.setAttribute('aria-label', title.textContent.trim());
        } else {
          item.setAttribute('aria-label', `Trust Badge ${index + 1}`);
        }

        item.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const button = item.querySelector('.trust-badge-button');
            if (button) {
              button.click();
            }
          }
        });

        item.addEventListener('focus', () => {
          item.style.outline = '2px solid #5c6ac4';
          item.style.outlineOffset = '4px';
        });

        item.addEventListener('blur', () => {
          item.style.outline = 'none';
        });
      });
    }

    addButtonEffects() {
      const buttons = document.querySelectorAll('.trust-badge-button');
      
      buttons.forEach(button => {
        button.addEventListener('click', (e) => {
          this.trackButtonClick(button);
        });

        button.addEventListener('mouseenter', () => {
          const icon = button.querySelector('.trust-badge-button-icon');
          if (icon) {
            icon.style.transform = 'translateX(3px)';
          }
        });

        button.addEventListener('mouseleave', () => {
          const icon = button.querySelector('.trust-badge-button-icon');
          if (icon) {
            icon.style.transform = 'translateX(0)';
          }
        });
      });
    }

    trackButtonClick(button) {
      const badgeItem = button.closest('.trust-badge-item');
      const title = badgeItem ? badgeItem.querySelector('.trust-badge-title')?.textContent : '';
      
      const event = new CustomEvent('trustBadgeButtonClick', {
        detail: {
          buttonText: button.textContent.trim(),
          badgeTitle: title,
          href: button.getAttribute('href')
        }
      });
      document.dispatchEvent(event);

      if (typeof gtag !== 'undefined') {
        gtag('event', 'click', {
          'event_category': 'Trust Badge',
          'event_label': title,
          'value': button.getAttribute('href')
        });
      }
    }
  }

  /**
   * 懒加载图片
   */
  class LazyLoadImages {
    constructor() {
      this.images = document.querySelectorAll('.trust-badge-image[loading="lazy"]');
      this.init();
    }

    init() {
      if (!this.images.length) return;

      if ('loading' in HTMLImageElement.prototype) {
        return;
      }

      if ('IntersectionObserver' in window) {
        this.observeImages();
      } else {
        this.loadAllImages();
      }
    }

    observeImages() {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            this.loadImage(img);
            imageObserver.unobserve(img);
          }
        });
      }, {
        rootMargin: '50px'
      });

      this.images.forEach(img => imageObserver.observe(img));
    }

    loadImage(img) {
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
    }

    loadAllImages() {
      this.images.forEach(img => this.loadImage(img));
    }
  }

  /**
   * 性能监控
   */
  class PerformanceMonitor {
    constructor() {
      this.metrics = {
        animationStart: 0,
        animationEnd: 0,
        totalDuration: 0
      };
    }

    startTracking() {
      if (typeof performance !== 'undefined' && performance.now) {
        this.metrics.animationStart = performance.now();
      }
    }

    endTracking() {
      if (typeof performance !== 'undefined' && performance.now) {
        this.metrics.animationEnd = performance.now();
        this.metrics.totalDuration = this.metrics.animationEnd - this.metrics.animationStart;
        
        if (this.isDesignMode()) {
          console.log('🎯 Trust Badges Performance:', {
            duration: `${this.metrics.totalDuration.toFixed(2)}ms`,
            fps: this.calculateFPS(),
            timestamp: new Date().toISOString()
          });
        }
      }
    }

    calculateFPS() {
      if (this.metrics.totalDuration === 0) return 'N/A';
      const fps = 1000 / (this.metrics.totalDuration / 60);
      return `${fps.toFixed(2)} FPS`;
    }

    isDesignMode() {
      return window.Shopify && window.Shopify.designMode;
    }
  }

  /**
   * 初始化所有功能
   */
  function initTrustBadges() {
    try {
      let perfMonitor = null;
      if (window.Shopify && window.Shopify.designMode) {
        perfMonitor = new PerformanceMonitor();
        perfMonitor.startTracking();
      }

      const animationController = new TrustBadgesAnimation();
      const lazyLoader = new LazyLoadImages();
      
      if (perfMonitor) {
        setTimeout(() => {
          perfMonitor.endTracking();
        }, 2000);
      }

      document.dispatchEvent(new CustomEvent('trustBadgesInitialized'));
      
    } catch (error) {
      console.error('Trust Badges initialization error:', error);
    }
  }

  /**
   * 页面加载完成后初始化
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTrustBadges);
  } else {
    initTrustBadges();
  }

  /**
   * Shopify 模板编辑器支持
   */
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      setTimeout(() => {
        const section = event.target.querySelector('.trust-badges-section');
        if (section) {
          new TrustBadgesAnimation();
          console.log('✅ Trust Badges section reloaded');
        }
      }, 100);
    });

    document.addEventListener('shopify:section:select', (event) => {
      const section = event.target.querySelector('.trust-badges-section');
      if (section) {
        section.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    });

    document.addEventListener('shopify:block:select', (event) => {
      const block = event.target;
      if (block && block.classList.contains('trust-badge-item')) {
        block.style.outline = '2px dashed #5c6ac4';
        block.style.outlineOffset = '4px';
        block.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest' 
        });
      }
    });

    document.addEventListener('shopify:block:deselect', (event) => {
      const block = event.target;
      if (block && block.classList.contains('trust-badge-item')) {
        block.style.outline = 'none';
      }
    });

    console.log('🎨 Trust Badges: Design Mode Active');
  }

  /**
   * 导出供外部使用
   */
  if (typeof module !== 'undefined' && typeof module.exports !== 'undefined') {
    module.exports = {
      TrustBadgesAnimation,
      LazyLoadImages,
      PerformanceMonitor,
      initTrustBadges
    };
  }

  /**
   * 全局访问（用于调试）
   */
  if (typeof window !== 'undefined') {
    window.TrustBadges = {
      version: '1.0.0',
      init: initTrustBadges,
      TrustBadgesAnimation,
      LazyLoadImages,
      PerformanceMonitor
    };
  }

})();
